import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Checkbox, ContentSwitcher, FilterableMultiSelect, Modal, PasswordInput, Select, SelectItem, Switch, Tag, TextArea, TextInput } from '@carbon/react';
import { Filter } from '@carbon/icons-react';
import { GAME_CATALOG, vnDayKey } from '@ruby-cms/shared';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { BlockSkeleton, EmptyState, ErrorState, InlineError } from '../components/states';
import { Money, When } from '../components/format';
import { CommandStatus, COMMAND_SOURCE, HE_A_ERRORS } from '../components/labels';
import type { Command } from './Commands';

// Player 360 and risk (P1-D03): what the game enforces on this player, risk marks, notes, money in and out,
// bonuses and games played.

// mkt: MKT account (docs/specs/mkt-tach-quy.md), not a play restriction: its money stays out of the game pools and the reports
export type PlayFlags = { locked: boolean; lockMessage: string; withdrawFrozen: boolean; chatMutedUntil: number | null; lockedGames: string[]; mkt?: boolean };
type Marks = { watchlist: boolean; suspectedBot: boolean; suspectedMulti: boolean; updatedAt: string; updatedByName: string };
type Note = { id: string; body: string; adminName: string; createdAt: string };
export type Risk = { flags: PlayFlags; marks: Marks | null; notes: Note[] };

const GAME_NAME = new Map(GAME_CATALOG.map((g) => [g.id, g.name]));
const gameName = (id: string) => GAME_NAME.get(id) ?? id;
const boDau = (s: string) => String(s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/đ/gi, 'd').toLowerCase();
// External lobbies (Bắn Cá, live casino halls, sports): the only games the per-player lock dialog lists
const PARTNER_GAMES = GAME_CATALOG.filter((g) => g.group === 'partner');
const PARTNER_IDS = new Set(PARTNER_GAMES.map((g) => g.id));

export function RiskTags({ marks }: { marks: Pick<Marks, 'watchlist' | 'suspectedBot' | 'suspectedMulti'> | null | undefined }) {
  if (!marks) return null;
  return <>
    {marks.watchlist ? <Tag type="magenta" size="sm">Theo dõi</Tag> : null}
    {marks.suspectedBot ? <Tag type="warm-gray" size="sm">Nghi bot</Tag> : null}
    {marks.suspectedMulti ? <Tag type="purple" size="sm">Nghi nhiều tài khoản</Tag> : null}
  </>;
}

export function useRisk(userId: number) {
  return useQuery({ queryKey: ['user-risk', userId], queryFn: () => api.get<Risk>(`/api/admin/users/${userId}/risk`) });
}

const MUTE_CHOICES: [string, string][] = [
  ['60', '1 giờ'], ['360', '6 giờ'], ['1440', '1 ngày'], ['4320', '3 ngày'], ['10080', '7 ngày'], ['43200', '30 ngày'], ['5256000', 'Vô thời hạn (10 năm)'],
];

export function commandError(c: Command | null | undefined): string | null {
  if (!c || c.status === 'SUCCEEDED') return null;
  if (c.status === 'PENDING') return 'Hệ A chưa trả lời, lệnh sẽ được gửi lại tự động.';
  return HE_A_ERRORS[c.lastError ?? ''] ?? c.lastError ?? 'Hệ A từ chối lệnh';
}

// Restrictions the game enforces: withdrawals, chat, per-game lock. One he A command per change.
export function RestrictDialog({ userId, flags, open, onClose }: { userId: number; flags: PlayFlags; open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const [mute, setMute] = useState('1440');
  const [games, setGames] = useState<string[] | null>(null);
  const [last, setLast] = useState<Command | null>(null);
  const picked = games ?? flags.lockedGames;
  const m = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post<{ command: Command; flags: PlayFlags }>(`/api/admin/users/${userId}/flags`, body),
    onSuccess: (r) => { setLast(r.command); setGames(null); qc.invalidateQueries({ queryKey: ['user-risk', userId] }); qc.invalidateQueries({ queryKey: ['user', userId] }); },
  });
  const items = GAME_CATALOG.map((g) => ({ id: g.id, text: `${g.name} (${g.id})` }));
  const err = commandError(last);
  return (
    <Modal open={open} passiveModal hasScrollingContent modalHeading="Hạn chế người chơi" onRequestClose={() => { setLast(null); onClose(); }}>
      <div className="stack">
        <p className="muted">Mỗi thay đổi là một lệnh gửi hệ A. Hệ A và hệ B chặn ngay tại chỗ người chơi thao tác: lệnh rút, khung chat, lần cược.</p>
        <section className="stack">
          <h4>Rút tiền</h4>
          <p>{flags.withdrawFrozen ? 'Đang dừng rút: lệnh rút mới bị từ chối, lệnh rút đang chờ không chi được.' : 'Người chơi rút tiền bình thường.'}</p>
          <div><Button size="sm" kind={flags.withdrawFrozen ? 'tertiary' : 'danger--tertiary'} disabled={m.isPending}
            onClick={() => m.mutate({ flag: 'withdraw', frozen: !flags.withdrawFrozen })}>{flags.withdrawFrozen ? 'Cho rút lại' : 'Dừng rút tiền'}</Button></div>
        </section>
        <section className="stack">
          <h4>Chat</h4>
          <p>{flags.chatMutedUntil ? <>Đang cấm chat đến <When v={flags.chatMutedUntil} />.</> : 'Người chơi chat bình thường.'}</p>
          <div className="row row--top">
            <Select id="mute-for" labelText="Cấm chat trong" size="sm" value={mute} onChange={(e) => setMute(e.target.value)}>
              {MUTE_CHOICES.map(([v, t]) => <SelectItem key={v} value={v} text={t} />)}
            </Select>
            <Button size="sm" kind="danger--tertiary" disabled={m.isPending} onClick={() => m.mutate({ flag: 'chat', muteMinutes: Number(mute) })}>Cấm chat</Button>
            {flags.chatMutedUntil ? <Button size="sm" kind="tertiary" disabled={m.isPending} onClick={() => m.mutate({ flag: 'chat', muteMinutes: 0 })}>Bỏ cấm chat</Button> : null}
          </div>
        </section>
        <section className="stack">
          <h4>Khóa game</h4>
          <FilterableMultiSelect
            id="lock-games" titleText="Game người chơi không được chơi" placeholder="Chọn game" items={items}
            direction="top"
            itemToString={(i) => (i ? i.text : '')}
            initialSelectedItems={items.filter((i) => picked.includes(i.id))}
            key={flags.lockedGames.join(',')}
            filterItems={(itemsList, { itemToString: toString, inputValue }) => {
              const list = [...itemsList];
              if (!inputValue) return list;
              const q = boDau(inputValue);
              return list.filter((item) => boDau(toString(item)).includes(q));
            }}
            onChange={({ selectedItems }) => setGames((selectedItems ?? []).map((i) => i.id))}
          />
          <div><Button size="sm" kind="primary" disabled={m.isPending || games === null}
            onClick={() => m.mutate({ flag: 'games', games: picked })}>Lưu danh sách game khóa</Button></div>
        </section>
        {last ? <p>Lệnh <Link className="cell-link" to={'/commands/' + last.id}>{COMMAND_SOURCE[last.sourceType ?? ''] ?? last.sourceType}</Link> <CommandStatus s={last.status} /></p> : null}
        {err ? <p className="text-error">{err}</p> : null}
        <InlineError error={m.error} />
      </div>
    </Modal>
  );
}

// Password reset: login (a temporary password shown once) or withdraw (removed; the player sets a new one).
// Per-game bet lock from the player list row: tick the partner games (external lobbies, group 'partner'),
// then confirm the change (locked and unlocked games listed) before the he A command goes out. Same command
// as the Khóa game section above; in-house games locked there stay in the saved list untouched.
// A plain checkbox list, not a dropdown: a dropdown menu inside the modal was clipped on a phone.
// The starting list is read fresh when the dialog opens, not taken from the list page, so a lock another
// operator saved since the list loaded is neither dropped nor unlocked unseen.
export function GameLockDialog({ player, onClose }: { player: { id: number; name: string }; onClose: () => void }) {
  const q = useQuery({ queryKey: ['user-risk', player.id], queryFn: () => api.get<Risk>(`/api/admin/users/${player.id}/risk`), refetchOnMount: 'always' });
  if (q.data && q.isFetchedAfterMount) return <GameLockForm player={{ ...player, lockedGames: q.data.flags.lockedGames }} onClose={onClose} />;
  return (
    <Modal open passiveModal modalHeading={`Khóa cược game: ${player.name}`} onRequestClose={onClose}>
      {q.error ? <ErrorState error={q.error} onRetry={() => q.refetch()} /> : <BlockSkeleton lines={3} />}
    </Modal>
  );
}

function GameLockForm({ player, onClose }: { player: { id: number; name: string; lockedGames: string[] }; onClose: () => void }) {
  const qc = useQueryClient();
  const [games, setGames] = useState<string[]>(player.lockedGames);
  const [confirm, setConfirm] = useState(false);
  const [last, setLast] = useState<Command | null>(null);
  const m = useMutation({
    mutationFn: () => api.post<{ command: Command; flags: PlayFlags }>(`/api/admin/users/${player.id}/flags`, { flag: 'games', games }),
    onSuccess: (r) => {
      setLast(r.command);
      qc.invalidateQueries({ queryKey: ['users'] });
      qc.invalidateQueries({ queryKey: ['user-risk', player.id] });
      if (r.command?.status === 'SUCCEEDED') onClose();
    },
  });
  const kept = games.filter((g) => !PARTNER_IDS.has(g));
  const lock = games.filter((g) => !player.lockedGames.includes(g));
  const unlock = player.lockedGames.filter((g) => !games.includes(g));
  const err = commandError(last);
  return (
    <Modal open hasScrollingContent modalHeading={`Khóa cược game: ${player.name}`} danger={confirm}
      primaryButtonText={confirm ? 'Xác nhận' : 'Tiếp tục'} secondaryButtonText={confirm ? 'Quay lại' : 'Hủy'}
      primaryButtonDisabled={m.isPending || (!lock.length && !unlock.length)}
      onRequestClose={onClose}
      onSecondarySubmit={() => (confirm ? setConfirm(false) : onClose())}
      onRequestSubmit={() => (confirm ? m.mutate() : setConfirm(true))}>
      <div className="stack">
        {confirm ? <>
          <p>Người chơi <strong>{player.name}</strong> (mã <span className="num">{player.id}</span>):</p>
          {lock.length ? <p>Không được cược: {lock.map((g) => <Tag key={g} size="sm" type="red">{gameName(g)}</Tag>)}</p> : null}
          {unlock.length ? <p>Mở khóa cược: {unlock.map((g) => <Tag key={g} size="sm" type="green">{gameName(g)}</Tag>)}</p> : null}
          <p className="muted">Hệ A và hệ B từ chối lần cược tiếp theo ở game bị khóa, trước khi trừ tiền. Game khác chơi bình thường.</p>
        </> : <>
          <p className="muted">Tích game đối tác người chơi không được đặt cược. Bỏ tích một game để mở khóa game đó.</p>
          <fieldset className="perm-grid" aria-label="Game bị khóa cược">
            {PARTNER_GAMES.map((g) => (
              <Checkbox key={g.id} id={`lock-${player.id}-${g.id}`} labelText={`${g.name} (${g.id})`} checked={games.includes(g.id)}
                onChange={(_e, { checked }) => setGames((cur) => (checked ? [...cur.filter((x) => x !== g.id), g.id] : cur.filter((x) => x !== g.id)))} />
            ))}
          </fieldset>
          {kept.length ? <p className="muted">Game của nhà đang khóa, giữ nguyên: {kept.map((g) => <Tag key={g} size="sm">{gameName(g)}</Tag>)}</p> : null}
        </>}
        {last ? <p>Lệnh <Link className="cell-link" to={'/commands/' + last.id}>{COMMAND_SOURCE[last.sourceType ?? ''] ?? last.sourceType}</Link> <CommandStatus s={last.status} /></p> : null}
        {err ? <p className="text-error">{err}</p> : null}
        <InlineError error={m.error} />
      </div>
    </Modal>
  );
}

// The player's own rule for a withdraw password (backend/lib/nap_rut.js mkHopLe)
const PIN_MIN = 4;
const PIN_MAX = 32;
// The game's own rule for a new login password (backend/server.js xuLyDoiMatKhau, API LOGIN_PASSWORD_MIN/MAX)
const LOGIN_MIN = 6;
const LOGIN_MAX = 128;

type PasswordKind = 'login_set' | 'login' | 'withdraw' | 'withdraw_set';

export function PasswordDialog({ userId, open, onClose }: { userId: number; open: boolean; onClose: () => void }) {
  const [kind, setKind] = useState<PasswordKind>('login_set');
  // a typed password lives only in this state: cleared when the request goes out and when the dialog closes
  const [pin, setPin] = useState('');
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: () => api.post<{ command: Command; tempPassword: string | null; pending?: boolean }>(`/api/admin/users/${userId}/password`,
      kind === 'withdraw_set' ? { kind, pin } : kind === 'login_set' ? { kind, password: pin } : { kind }),
    onSettled: () => setPin(''),
    // a reset is a security change that holds withdrawals (P1-D19): the bank tab lists it
    onSuccess: () => qc.invalidateQueries({ queryKey: ['user-bank', userId] }),
  });
  const close = () => { m.reset(); setPin(''); setKind('login_set'); onClose(); };
  const done = m.data;
  const err = commandError(done?.command);
  const typed = kind === 'withdraw_set' || kind === 'login_set';
  const pinBad = kind === 'withdraw_set' ? pin.length < PIN_MIN || pin.length > PIN_MAX
    : kind === 'login_set' ? pin.length < LOGIN_MIN || pin.length > LOGIN_MAX || /\s/.test(pin) : false;
  const loginRule = `Từ ${LOGIN_MIN} đến ${LOGIN_MAX} ký tự, không có khoảng trắng`;
  return (
    <Modal open={open} modalHeading="Đặt lại mật khẩu người chơi" primaryButtonText={done ? 'Xong' : typed ? 'Đặt mật khẩu' : 'Đặt lại'} secondaryButtonText="Hủy"
      primaryButtonDisabled={m.isPending || (!done && pinBad)} onRequestClose={close} onRequestSubmit={() => (done ? close() : m.mutate())}>
      <div className="stack">
        {!done ? <>
          <Select id="pw-kind" labelText="Mật khẩu" value={kind} disabled={m.isPending} onChange={(e) => { setKind(e.target.value as PasswordKind); setPin(''); }}>
            <SelectItem value="login_set" text="Mật khẩu đăng nhập: đặt mật khẩu mới" />
            <SelectItem value="login" text="Mật khẩu đăng nhập: cấp mật khẩu tạm" />
            <SelectItem value="withdraw" text="Mật khẩu rút: xóa, lần rút sau người chơi đặt lại" />
            <SelectItem value="withdraw_set" text="Mật khẩu rút: đặt mật khẩu mới cho người chơi" />
          </Select>
          {kind === 'login_set' ? (
            <PasswordInput id="pw-login" labelText="Mật khẩu đăng nhập mới" autoComplete="new-password" disabled={m.isPending} showPasswordLabel="Hiện mật khẩu" hidePasswordLabel="Ẩn mật khẩu"
              helperText="Gõ mật khẩu mới rồi bấm Đặt mật khẩu; gửi đúng mật khẩu này cho khách."
              invalid={pin.length > 0 && pinBad} invalidText={`Mật khẩu đăng nhập: ${loginRule.toLowerCase()}`}
              value={pin} onChange={(e) => setPin(e.target.value)} />
          ) : null}
          {kind === 'withdraw_set' ? (
            <PasswordInput id="pw-pin" labelText="Mật khẩu rút mới" autoComplete="off" disabled={m.isPending} showPasswordLabel="Hiện mật khẩu" hidePasswordLabel="Ẩn mật khẩu"
              helperText={`Từ ${PIN_MIN} đến ${PIN_MAX} ký tự. Thay mật khẩu rút cũ, xóa số lần nhập sai và gỡ khóa nhập sai. CMS không lưu và không hiện lại mật khẩu này.`}
              invalid={pin.length > 0 && pinBad} invalidText={`Mật khẩu rút từ ${PIN_MIN} đến ${PIN_MAX} ký tự`}
              value={pin} onChange={(e) => setPin(e.target.value)} />
          ) : null}
          <p>Người chơi bị đăng xuất khỏi mọi thiết bị và mọi kết nối game bị ngắt.</p>
        </> : null}
        {done?.tempPassword ? <>
          <p>Mật khẩu tạm (chỉ hiện một lần, CMS không lưu):</p>
          <p><strong className="num" style={{ fontSize: 20, userSelect: 'all' }}>{done.tempPassword}</strong></p>
          <p className="muted">Gửi cho người chơi qua kênh CSKH đã xác minh. Người chơi nên đổi mật khẩu sau khi đăng nhập.</p>
          {done.command?.status === 'PENDING' ? <p className="muted">Hệ A chưa trả lời: mật khẩu này có hiệu lực khi lệnh được gửi lại thành công. Ghi lại ngay, CMS không hiện lại.</p> : null}
        </> : null}
        {done?.pending ? <p className="text-error">Hệ thống game chưa xác nhận lệnh; CMS tự gửi lại. Mật khẩu mới có hiệu lực khi lệnh ở trạng thái Thành công.</p> : null}
        {done && !done.tempPassword && !done.pending && !err ? <p>{kind === 'login_set' ? 'Đã đặt mật khẩu đăng nhập mới và đăng xuất người chơi. Gửi đúng mật khẩu vừa gõ cho người chơi qua kênh CSKH đã xác minh.'
          : kind === 'withdraw_set' ? 'Đã đặt mật khẩu rút mới và đăng xuất người chơi. Gửi mật khẩu cho người chơi qua kênh CSKH đã xác minh.' : 'Đã xóa mật khẩu rút và đăng xuất người chơi.'}</p> : null}
        {err ? <p className="text-error">{err}</p> : null}
        <InlineError error={m.error} />
      </div>
    </Modal>
  );
}

// Flags, marks and notes: the first thing support reads about a player.
export function RiskPanel({ userId }: { userId: number }) {
  const { can } = useAuth();
  const qc = useQueryClient();
  const q = useRisk(userId);
  const [note, setNote] = useState('');
  const marks = useMutation({
    mutationFn: (b: { watchlist: boolean; suspectedBot: boolean; suspectedMulti: boolean }) => api.post(`/api/admin/users/${userId}/marks`, b),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['user-risk', userId] }),
  });
  const addNote = useMutation({
    mutationFn: () => api.post(`/api/admin/users/${userId}/notes`, { body: note }),
    onSuccess: () => { setNote(''); qc.invalidateQueries({ queryKey: ['user-risk', userId] }); },
  });
  if (q.isLoading) return <BlockSkeleton lines={5} />;
  if (q.error || !q.data) return <ErrorState error={q.error ?? new Error('Không đọc được')} onRetry={() => q.refetch()} />;
  const { flags, notes } = q.data;
  const mk = q.data.marks ?? { watchlist: false, suspectedBot: false, suspectedMulti: false };
  const set = (k: 'watchlist' | 'suspectedBot' | 'suspectedMulti', v: boolean) => marks.mutate({ watchlist: mk.watchlist, suspectedBot: mk.suspectedBot, suspectedMulti: mk.suspectedMulti, [k]: v });
  return (
    <div className="stack">
      <h3>Game đang chặn</h3>
      <dl className="facts">
        <dt>Tài khoản</dt><dd>{flags.locked ? <><Tag type="red" size="sm">Đã khóa</Tag> {flags.lockMessage ? <>Người chơi thấy: “{flags.lockMessage}”</> : <span className="muted">Không có lời nhắn</span>}</> : 'Hoạt động'}</dd>
        <dt>Rút tiền</dt><dd>{flags.withdrawFrozen ? <Tag type="red" size="sm">Dừng rút</Tag> : 'Bình thường'}</dd>
        <dt>Chat</dt><dd>{flags.chatMutedUntil ? <>Cấm đến <When v={flags.chatMutedUntil} /></> : 'Bình thường'}</dd>
        <dt>Game bị khóa</dt><dd>{flags.lockedGames.length ? flags.lockedGames.map((g) => <Tag key={g} size="sm" type="cool-gray">{gameName(g)}</Tag>) : 'Không'}</dd>
      </dl>
      <h3>Cờ rủi ro</h3>
      <p className="muted">Chỉ để đánh dấu cho CSKH và kế toán (hiện ở danh sách và hàng duyệt rút), không đổi cách chơi.</p>
      <div className="row">
        <Checkbox id="mk-watch" labelText="Theo dõi" checked={mk.watchlist} disabled={!can('users.risk') || marks.isPending} onChange={(_e, { checked }) => set('watchlist', checked)} />
        <Checkbox id="mk-bot" labelText="Nghi bot" checked={mk.suspectedBot} disabled={!can('users.risk') || marks.isPending} onChange={(_e, { checked }) => set('suspectedBot', checked)} />
        <Checkbox id="mk-multi" labelText="Nghi nhiều tài khoản" checked={mk.suspectedMulti} disabled={!can('users.risk') || marks.isPending} onChange={(_e, { checked }) => set('suspectedMulti', checked)} />
      </div>
      <InlineError error={marks.error} />
      <h3>Ghi chú</h3>
      {can('users.risk') ? (
        <div className="stack">
          <TextArea id="note" labelText="Thêm ghi chú" rows={2} maxCount={2000} enableCounter value={note} onChange={(e) => setNote(e.target.value)} />
          <div><Button size="sm" disabled={!note.trim() || addNote.isPending} onClick={() => addNote.mutate()}>Lưu ghi chú</Button></div>
          <InlineError error={addNote.error} />
        </div>
      ) : null}
      {notes.length ? (
        <ul className="list">
          {notes.map((n) => <li key={n.id}><span style={{ whiteSpace: 'pre-wrap' }}>{n.body}</span> <span className="muted">· <When v={n.createdAt} /></span></li>)}
        </ul>
      ) : <EmptyState title="Chưa có ghi chú" />}
    </div>
  );
}

type WalletRow = { id: string; huong: 'nap' | 'rut'; kenh: string; so_tien: number; trang_thai: string; tao_luc: string; xu_ly_luc: string | null; ngan_hang: string | null; so_tk: string | null; ten_tk: string | null; ly_do: string | null };
export type WalletData = { rows: WalletRow[]; totals: { deposited: number; withdrawn: number; pendingWithdraw: number; deposits: number; withdrawals: number } };
export const WALLET_STATE: Record<string, string> = {
  cho: 'Chờ', da_bao: 'Người chơi báo đã chuyển', dang_tra: 'Đang chi', da_tra: 'Đã chi', da_duyet: 'Đã duyệt', huy: 'Hủy / hoàn', het_han: 'Hết hạn', nguoi_huy: 'Người chơi hủy',
};

export function WalletPanel({ userId, frozen }: { userId: number; frozen: boolean }) {
  const q = useQuery({ queryKey: ['user-wallet', userId], queryFn: () => api.get<WalletData>(`/api/admin/users/${userId}/wallet`) });
  if (q.isLoading) return <BlockSkeleton lines={4} />;
  if (q.error || !q.data) return <ErrorState error={q.error ?? new Error('Không đọc được')} onRetry={() => q.refetch()} />;
  const { rows, totals } = q.data;
  return (
    <div className="stack">
      <div className="kpis">
        <div className="kpi"><span>Đã nạp ({totals.deposits} lần)</span><strong><Money v={totals.deposited} unit /></strong></div>
        <div className="kpi"><span>Đã rút ({totals.withdrawals} lần)</span><strong><Money v={totals.withdrawn} unit /></strong></div>
        <div className="kpi"><span>Nạp trừ rút</span><strong><Money v={totals.deposited - totals.withdrawn} signed unit /></strong></div>
        <div className="kpi"><span>Rút đang chờ{frozen ? ' (đang giữ)' : ''}</span><strong><Money v={totals.pendingWithdraw} unit /></strong></div>
      </div>
      {rows.length ? (
        <table className="cds--data-table cds--data-table--sm">
          <thead><tr><th>Tạo lúc</th><th>Loại</th><th>Kênh</th><th>Số tiền</th><th>Trạng thái</th><th>Tài khoản</th><th>Xử lý lúc</th><th>Mã lệnh</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td data-label="Tạo lúc"><When v={r.tao_luc} /></td><td data-label="Loại">{r.huong === 'nap' ? 'Nạp' : 'Rút'}</td><td data-label="Kênh">{r.kenh === 'usdt' ? 'USDT' : 'Ngân hàng'}</td>
                <td data-label="Số tiền"><Money v={r.so_tien} /></td><td data-label="Trạng thái">{WALLET_STATE[r.trang_thai] ?? r.trang_thai}{r.ly_do ? <span className="muted"> ({r.ly_do})</span> : null}</td>
                <td data-label="Tài khoản">{r.ngan_hang ?? ''} <span className="num">{r.so_tk ?? ''}</span>{r.ten_tk ? <span className="muted"> {r.ten_tk}</span> : null}</td><td data-label="Xử lý lúc">{r.xu_ly_luc ? <When v={r.xu_ly_luc} /> : '-'}</td>
                <td data-label="Mã lệnh"><Link className="cell-link num" to={(r.huong === 'nap' ? '/wallet/deposits' : '/wallet/withdrawals') + '?status=&ref=' + r.id}>{r.id}</Link></td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : <EmptyState title="Chưa có lệnh nạp hay rút" />}
    </div>
  );
}

export type BonusCms = { id: string; key: string; sourceType: string | null; reason: string; delta: number; currency: 'vin' | 'xu'; wagering: string | null; completedAt: string | null; createdAt: string };
type BonusPromo = { created_at: string; service_name: string; delta: number };

export function BonusesPanel({ userId }: { userId: number }) {
  const q = useQuery({ queryKey: ['user-bonuses', userId], queryFn: () => api.get<{ cms: BonusCms[]; promo: BonusPromo[] }>(`/api/admin/users/${userId}/bonuses`) });
  if (q.isLoading) return <BlockSkeleton lines={4} />;
  if (q.error || !q.data) return <ErrorState error={q.error ?? new Error('Không đọc được')} onRetry={() => q.refetch()} />;
  const { cms, promo } = q.data;
  const total = cms.filter((c) => c.currency === 'vin').reduce((a, c) => a + c.delta, 0) + promo.reduce((a, p) => a + p.delta, 0);
  const totalXu = cms.filter((c) => c.currency === 'xu').reduce((a, c) => a + c.delta, 0);
  if (!cms.length && !promo.length) return <EmptyState title="Chưa nhận khoản thưởng nào" />;
  return (
    <div className="stack">
      <div className="kpis">
        <div className="kpi"><span>Tổng thưởng tiền thật (khuyến mãi nạp đã trừ phần bị thu hồi)</span><strong><Money v={total} unit /></strong></div>
        {totalXu ? <div className="kpi"><span>Tổng thưởng xu</span><strong>{totalXu.toLocaleString('vi-VN')} xu</strong></div> : null}
      </div>
      <table className="cds--data-table cds--data-table--sm">
        <thead><tr><th>Lúc</th><th>Nguồn</th><th>Số tiền</th><th>Vòng cược</th><th>Chi tiết</th></tr></thead>
        <tbody>
          {cms.map((c) => (
            <tr key={c.id}><td data-label="Lúc"><When v={c.completedAt ?? c.createdAt} /></td><td data-label="Nguồn">{COMMAND_SOURCE[c.sourceType ?? ''] ?? c.sourceType ?? 'CMS'}</td>
              <td data-label="Số tiền">{c.currency === 'xu' ? <span className="num">+{c.delta.toLocaleString('vi-VN')} xu</span> : <Money v={c.delta} signed />}</td><td data-label="Vòng cược">{c.wagering ? 'Có' : '-'}</td><td data-label="Chi tiết"><Link className="cell-link" to={'/commands/' + c.id}>Lệnh CMS</Link> <span className="muted">{c.reason}</span></td></tr>
          ))}
          {promo.map((p, i) => (
            <tr key={'km' + i}><td data-label="Lúc"><When v={p.created_at} /></td><td data-label="Nguồn">Khuyến mãi nạp</td><td data-label="Số tiền"><Money v={p.delta} signed /></td><td data-label="Vòng cược">Có</td><td data-label="Chi tiết">{p.service_name}</td></tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const shift = (d: string, n: number) => new Date(new Date(d + 'T00:00:00Z').getTime() + n * 86_400_000).toISOString().slice(0, 10);

type DayRow = { day: string; bets: number; bet: number; win: number; refund: number; pl: number };
type GameRow = { game: string; bets: number; days: number; first: string | null; last: string | null; bet: number; win: number; refund: number; pl: number; pl30: number; bet30: number };
export type GamesData = {
  items: GameRow[];
  totals: { bet: number; win: number; refund: number; pl: number; pl30: number; bet30: number; bets: number };
  byDay: DayRow[];
  range: { from: string; to: string } | null;
  today: string;
};

type Preset = 'all' | 'today' | 'yesterday' | '7d' | '30d' | 'custom';
type ViewMode = 'game' | 'day';

export function GamesPanel({ userId }: { userId: number }) {
  const [preset, setPreset] = useState<Preset>('all');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('game');

  const q = useQuery({
    queryKey: ['user-games', userId, from, to],
    queryFn: () => api.get<GamesData>(`/api/admin/users/${userId}/games`, {
      ...(from ? { from } : {}),
      ...(to ? { to } : {}),
    }),
  });

  const today = q.data?.today ?? vnDayKey(new Date());

  const pickPreset = (p: Preset) => {
    setPreset(p);
    if (p === 'all') {
      setFrom('');
      setTo('');
      setCustomFrom('');
      setCustomTo('');
    } else if (p === 'today') {
      setFrom(today);
      setTo(today);
      setCustomFrom(today);
      setCustomTo(today);
    } else if (p === 'yesterday') {
      const yd = shift(today, -1);
      setFrom(yd);
      setTo(yd);
      setCustomFrom(yd);
      setCustomTo(yd);
    } else if (p === '7d') {
      const d7 = shift(today, -6);
      setFrom(d7);
      setTo(today);
      setCustomFrom(d7);
      setCustomTo(today);
    } else if (p === '30d') {
      const d30 = shift(today, -29);
      setFrom(d30);
      setTo(today);
      setCustomFrom(d30);
      setCustomTo(today);
    }
  };

  const applyCustom = (e: React.FormEvent) => {
    e.preventDefault();
    if (!customFrom && !customTo) {
      pickPreset('all');
      return;
    }
    const f = customFrom || customTo;
    const t = customTo || today;
    setPreset('custom');
    setFrom(f);
    setTo(t);
  };

  const isAllTime = !from && !to;
  const items = q.data?.items ?? [];
  const totals = q.data?.totals;
  const byDay = q.data?.byDay ?? [];
  const hasData = items.length > 0;

  return (
    <div className="stack">
      <div className="toolbar-filters" role="group" aria-label="Bộ lọc cược theo ngày">
        <div className="row presets">
          <Button kind={preset === 'all' ? 'secondary' : 'ghost'} size="sm" onClick={() => pickPreset('all')}>Tất cả</Button>
          <Button kind={preset === 'today' ? 'secondary' : 'ghost'} size="sm" onClick={() => pickPreset('today')}>Hôm nay</Button>
          <Button kind={preset === 'yesterday' ? 'secondary' : 'ghost'} size="sm" onClick={() => pickPreset('yesterday')}>Hôm qua</Button>
          <Button kind={preset === '7d' ? 'secondary' : 'ghost'} size="sm" onClick={() => pickPreset('7d')}>7 ngày</Button>
          <Button kind={preset === '30d' ? 'secondary' : 'ghost'} size="sm" onClick={() => pickPreset('30d')}>30 ngày</Button>
        </div>
        <form className="row" onSubmit={applyCustom} style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <TextInput
            id="gp-from"
            type="date"
            labelText="Từ ngày"
            value={customFrom}
            max={customTo || today}
            onChange={(e) => setCustomFrom(e.target.value)}
          />
          <TextInput
            id="gp-to"
            type="date"
            labelText="Đến ngày"
            value={customTo}
            min={customFrom}
            max={today}
            onChange={(e) => setCustomTo(e.target.value)}
          />
          <Button type="submit" kind="tertiary" size="md" renderIcon={Filter}>Lọc</Button>
        </form>
        <span className="toolbar-filters__grow" />
        <ContentSwitcher size="sm" selectedIndex={viewMode === 'game' ? 0 : 1} onChange={(e) => setViewMode(e.name === 'day' ? 'day' : 'game')}>
          <Switch name="game" text="Theo game" />
          <Switch name="day" text="Theo ngày" />
        </ContentSwitcher>
      </div>

      {q.isLoading ? (
        <BlockSkeleton lines={4} />
      ) : q.error ? (
        <ErrorState error={q.error} onRetry={() => q.refetch()} />
      ) : !hasData ? (
        isAllTime ? (
          <EmptyState title="Chưa chơi game nào" body="Số liệu lấy từ thống kê ngày, cập nhật vài phút một lần." />
        ) : (
          <EmptyState
            title="Không có dữ liệu cược trong khoảng thời gian này"
            body="Chọn khoảng ngày khác hoặc bấm 'Tất cả' để xem toàn bộ lịch sử."
          />
        )
      ) : (
        <>
          {totals ? (
            <div className="kpis">
              <div className="kpi">
                <span>Tổng cược ({totals.bets.toLocaleString('vi-VN')} lượt)</span>
                <strong><Money v={totals.bet} unit /></strong>
              </div>
              <div className="kpi">
                <span>Người chơi lãi / lỗ</span>
                <strong><Money v={totals.pl} signed unit /></strong>
              </div>
              {isAllTime ? (
                <>
                  <div className="kpi">
                    <span>Lãi / lỗ 30 ngày</span>
                    <strong><Money v={totals.pl30} signed unit /></strong>
                  </div>
                  <div className="kpi">
                    <span>Cược 30 ngày</span>
                    <strong><Money v={totals.bet30} unit /></strong>
                  </div>
                </>
              ) : (
                <>
                  <div className="kpi">
                    <span>Tiền thắng</span>
                    <strong><Money v={totals.win} unit /></strong>
                  </div>
                  <div className="kpi">
                    <span>Hoàn cược</span>
                    <strong><Money v={totals.refund} unit /></strong>
                  </div>
                </>
              )}
            </div>
          ) : null}

          <p className="muted">
            Lãi / lỗ của người chơi = tiền thắng + tiền hoàn - tiền cược. Số dương là người chơi đang thắng nhà cái.
            {isAllTime && viewMode === 'day' ? ' Thống kê theo ngày hiển thị tối đa 90 ngày chơi gần nhất.' : ''}
          </p>

          {viewMode === 'game' ? (
            <table className="cds--data-table cds--data-table--sm">
              <thead>
                <tr>
                  <th>Game</th>
                  <th>Lượt cược</th>
                  <th>Số ngày chơi</th>
                  <th>Chơi lần đầu / gần nhất</th>
                  <th>Tiền cược</th>
                  <th>Tiền thắng</th>
                  <th>Hoàn</th>
                  <th>Lãi / lỗ</th>
                  {isAllTime ? <th>Lãi / lỗ 30 ngày</th> : null}
                </tr>
              </thead>
              <tbody>
                {items.map((g) => (
                  <tr key={g.game}>
                    <td data-label="Game">{g.game}</td>
                    <td data-label="Lượt cược" className="num">{g.bets.toLocaleString('vi-VN')}</td>
                    <td data-label="Số ngày chơi" className="num">{g.days}</td>
                    <td data-label="Chơi lần đầu / gần nhất">
                      {g.first ? <When v={g.first} /> : '—'} / {g.last ? <When v={g.last} /> : '—'}
                    </td>
                    <td data-label="Tiền cược"><Money v={g.bet} /></td>
                    <td data-label="Tiền thắng"><Money v={g.win} /></td>
                    <td data-label="Hoàn"><Money v={g.refund} /></td>
                    <td data-label="Lãi / lỗ"><Money v={g.pl} signed /></td>
                    {isAllTime ? <td data-label="Lãi / lỗ 30 ngày"><Money v={g.pl30} signed /></td> : null}
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <table className="cds--data-table cds--data-table--sm">
              <thead>
                <tr>
                  <th>Ngày</th>
                  <th>Lượt cược</th>
                  <th>Tiền cược</th>
                  <th>Tiền thắng</th>
                  <th>Hoàn</th>
                  <th>Lãi / lỗ</th>
                </tr>
              </thead>
              <tbody>
                {byDay.map((d) => (
                  <tr key={d.day}>
                    <td data-label="Ngày" className="num is-title">{d.day}</td>
                    <td data-label="Lượt cược" className="num">{d.bets.toLocaleString('vi-VN')}</td>
                    <td data-label="Tiền cược"><Money v={d.bet} /></td>
                    <td data-label="Tiền thắng"><Money v={d.win} /></td>
                    <td data-label="Hoàn"><Money v={d.refund} /></td>
                    <td data-label="Lãi / lỗ"><Money v={d.pl} signed /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
}

// Lock or unlock many players at once (linked accounts, a ring); the lock message is shown to each at login.
export function BulkLockDialog({ ids: initial, open, onClose }: { ids: number[]; open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const [text, setText] = useState('');
  const [locked, setLocked] = useState(true);
  const [message, setMessage] = useState('');
  const shown = text || initial.join(', ');
  const ids = [...new Set(shown.split(/[\s,;]+/).map((x) => Number(x)).filter((x) => Number.isSafeInteger(x) && x > 0))];
  const m = useMutation({
    mutationFn: () => api.post<{ items: { userId: number; status: string; lastError: string | null; commandId: string }[] }>('/api/admin/users/bulk-lock', { ids, locked, ...(locked && message.trim() ? { message: message.trim() } : {}) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['users'] }); qc.invalidateQueries({ queryKey: ['user'] }); qc.invalidateQueries({ queryKey: ['user-linked'] }); },
  });
  const close = () => { m.reset(); setText(''); setMessage(''); onClose(); };
  return (
    <Modal open={open} danger={locked} modalHeading={locked ? 'Khóa nhiều tài khoản' : 'Mở khóa nhiều tài khoản'}
      primaryButtonText={m.data ? 'Xong' : `${locked ? 'Khóa' : 'Mở khóa'} ${ids.length} tài khoản`} secondaryButtonText="Hủy"
      primaryButtonDisabled={m.isPending || (!m.data && (!ids.length || ids.length > 200))} onRequestClose={close} onRequestSubmit={() => (m.data ? close() : m.mutate())}>
      <div className="stack">
        {!m.data ? <>
          <Select id="bulk-kind" labelText="Thao tác" value={locked ? 'lock' : 'unlock'} onChange={(e) => setLocked(e.target.value === 'lock')}>
            <SelectItem value="lock" text="Khóa: đăng xuất mọi thiết bị, giữ lệnh rút đang chờ" />
            <SelectItem value="unlock" text="Mở khóa" />
          </Select>
          <TextArea id="bulk-ids" labelText="Mã người chơi (cách nhau bởi dấu phẩy hoặc xuống dòng, tối đa 200)" rows={3} value={shown} onChange={(e) => setText(e.target.value)} />
          {locked ? <TextArea id="bulk-msg" labelText="Lời nhắn người chơi thấy khi đăng nhập (tùy chọn)" rows={2} maxCount={200} enableCounter value={message} onChange={(e) => setMessage(e.target.value)} /> : null}
        </> : (
          <ul className="list">
            {m.data.items.map((i) => <li key={i.userId}><Link className="cell-link" to={'/users/' + i.userId}>#{i.userId}</Link> <CommandStatus s={i.status} /> {i.lastError ? <span className="muted">{HE_A_ERRORS[i.lastError] ?? i.lastError}</span> : null}</li>)}
          </ul>
        )}
        <InlineError error={m.error} />
      </div>
    </Modal>
  );
}
